import {
	Badge,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Skeleton,
} from "@ancatag/at-design";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { cn } from "../../../lib/utils";
import {
	type GenerationType,
	listProviderTemplates,
	type ProviderTemplate,
} from "../../../services/provider-templates";

interface Step2ProviderProps {
	generationType: GenerationType;
	selectedProvider: ProviderTemplate | null;
	onSelect: (provider: ProviderTemplate, isHosted: boolean) => void;
}

export function Step2Provider({
	generationType,
	selectedProvider,
	onSelect,
}: Step2ProviderProps) {
	const [providers, setProviders] = useState<ProviderTemplate[]>([]);
	const [loading, setLoading] = useState(true);

	// Check if Nova-hosted is enabled via feature flag
	const isNovaHostedEnabled =
		import.meta.env.VITE_NOVA_HOSTED_ENABLED === "true" ||
		(window as any).__NOVA_CONFIG__?.features?.novaHosted === true;

	const loadProviders = useCallback(async () => {
		setLoading(true);
		try {
			const templates = await listProviderTemplates({
				generationType,
				isActive: true,
			});
			setProviders(templates);
		} catch (_error: any) {
			toast.error("Failed to load providers");
		} finally {
			setLoading(false);
		}
	}, [generationType]);

	useEffect(() => {
		loadProviders();
	}, [loadProviders]);

	const handleProviderSelect = (
		provider: ProviderTemplate,
		isHosted: boolean,
	) => {
		onSelect(provider, isHosted);
	};

	if (loading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-32 w-full" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground mb-6">
				Select a provider for {generationType} generation. Choose self-hosted or
				Nova-provided options.
			</p>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{providers.map((provider) => {
					const isSelected = selectedProvider?.id === provider.id;
					const canUseHosted =
						provider.isHostedAvailable && isNovaHostedEnabled;

					return (
						<div key={provider.id} className="space-y-2">
							{/* Self-Hosted Option */}
							<Card
								className={cn(
									"cursor-pointer transition-all hover:shadow-md min-h-[140px] flex flex-col",
									isSelected && !canUseHosted && "ring-2 ring-primary",
								)}
								onClick={() => handleProviderSelect(provider, false)}
							>
								<CardHeader>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<CardTitle className="text-lg">
													{provider.name}
												</CardTitle>
												<Badge variant="outline" className="text-xs">
													Self-Hosted
												</Badge>
											</div>
											<CardDescription className="mt-1">
												{provider.description ||
													`Configure ${provider.name} for ${generationType} generation`}
											</CardDescription>
										</div>
									</div>
								</CardHeader>
								<CardContent>
									<div className="flex flex-wrap gap-2 mt-2">
										{provider.features?.streaming && (
											<Badge variant="outline" className="text-xs">
												Streaming
											</Badge>
										)}
										{provider.features?.functionCalling && (
											<Badge variant="outline" className="text-xs">
												Functions
											</Badge>
										)}
										{provider.features?.vision && (
											<Badge variant="outline" className="text-xs">
												Vision
											</Badge>
										)}
									</div>
								</CardContent>
							</Card>

							{/* Nova-Hosted Option (if available) */}
							{canUseHosted ? (
								<Card
									className={cn(
										"cursor-pointer transition-all hover:shadow-md min-h-[140px] flex flex-col border-primary/20",
										isSelected && canUseHosted && "ring-2 ring-primary",
									)}
									onClick={() => handleProviderSelect(provider, true)}
								>
									<CardHeader>
										<div className="flex items-start justify-between">
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<CardTitle className="text-lg">
														{provider.name}
													</CardTitle>
													<Badge variant="default" className="text-xs">
														Nova-Provided
													</Badge>
												</div>
												<CardDescription className="mt-1">
													Nova manages credentials and infrastructure. No API
													keys needed.
												</CardDescription>
											</div>
										</div>
									</CardHeader>
									<CardContent>
										<div className="flex flex-wrap gap-2 mt-2">
											{provider.features?.streaming && (
												<Badge variant="outline" className="text-xs">
													Streaming
												</Badge>
											)}
											{provider.features?.functionCalling && (
												<Badge variant="outline" className="text-xs">
													Functions
												</Badge>
											)}
											{provider.features?.vision && (
												<Badge variant="outline" className="text-xs">
													Vision
												</Badge>
											)}
										</div>
									</CardContent>
								</Card>
							) : provider.isHostedAvailable ? (
								<Card className="opacity-50 cursor-not-allowed min-h-[140px] flex flex-col">
									<CardHeader>
										<div className="flex items-start justify-between">
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<CardTitle className="text-lg">
														{provider.name}
													</CardTitle>
													<Badge variant="secondary" className="text-xs">
														Coming Soon
													</Badge>
												</div>
												<CardDescription className="mt-1">
													Nova-hosted option will be available soon.
												</CardDescription>
											</div>
										</div>
									</CardHeader>
								</Card>
							) : null}
						</div>
					);
				})}
			</div>

			{providers.length === 0 && (
				<div className="text-center py-8 text-muted-foreground">
					No providers available for {generationType} generation.
				</div>
			)}
		</div>
	);
}
